import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { calculateSavingsGoal } from '@/utils/supreme-intelligence/savingsProjections';
import { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import { Calendar, DollarSign, Target, TrendingUp } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface SavingsGoalCalculatorProps {
  formData: EnhancedUnifiedFormData;
  estimatedMonthlyPayment: number;
}

export const SavingsGoalCalculator: React.FC<SavingsGoalCalculatorProps> = ({
  formData,
  estimatedMonthlyPayment
}) => {
  const [targetDate, setTargetDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 2);
    return date.toISOString().split('T')[0];
  });
  const [currentSavings, setCurrentSavings] = useState(0);

  const savingsGoal = calculateSavingsGoal(
    formData,
    new Date(targetDate),
    currentSavings,
    estimatedMonthlyPayment
  );

  const progressPercent = (currentSavings / savingsGoal.totalNeeded) * 100;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 text-foreground">Savings Goal Calculator</h3>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <Label htmlFor="targetDate">Target Purchase Date</Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="targetDate"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="currentSavings">Current Savings</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="currentSavings"
                type="number"
                value={currentSavings}
                onChange={(e) => setCurrentSavings(Number(e.target.value))}
                className="pl-10"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Progress to Goal</span>
              <span className="font-medium text-foreground">{progressPercent.toFixed(1)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="text-sm text-muted-foreground mb-1">Down Payment</div>
              <div className="text-lg font-bold text-foreground">${savingsGoal.downPaymentAmount.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">{savingsGoal.downPaymentPercentage}% of home price</div>
            </div>

            <div className="p-4 rounded-lg bg-muted/50">
              <div className="text-sm text-muted-foreground mb-1">Closing Costs</div>
              <div className="text-lg font-bold text-foreground">${savingsGoal.closingCostsEstimate.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">~3% of home price</div>
            </div>

            <div className="p-4 rounded-lg bg-muted/50">
              <div className="text-sm text-muted-foreground mb-1">Emergency Fund</div>
              <div className="text-lg font-bold text-foreground">${savingsGoal.emergencyFundRecommended.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">3 months expenses</div>
            </div>
          </div>

          <div className="p-6 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Total Savings Needed</h4>
            </div>
            <div className="text-3xl font-bold text-foreground mb-1">${savingsGoal.totalNeeded.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">
              Save ${(savingsGoal.totalNeeded - currentSavings).toLocaleString()} more in {savingsGoal.monthsUntilTarget} months
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg border border-border">
              <div className="text-sm text-muted-foreground mb-1">Monthly</div>
              <div className="text-xl font-bold text-foreground">${savingsGoal.monthlyContributionNeeded.toLocaleString()}</div>
            </div>

            <div className="text-center p-4 rounded-lg border border-border">
              <div className="text-sm text-muted-foreground mb-1">Bi-weekly</div>
              <div className="text-xl font-bold text-foreground">${savingsGoal.biweeklyContributionNeeded.toLocaleString()}</div>
            </div>

            <div className="text-center p-4 rounded-lg border border-border">
              <div className="text-sm text-muted-foreground mb-1">Weekly</div>
              <div className="text-xl font-bold text-foreground">${savingsGoal.weeklyContributionNeeded.toLocaleString()}</div>
            </div>
          </div>

          {savingsGoal.interestEarned > 0 && (
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="font-medium text-green-900 dark:text-green-100">High-Yield Savings Boost</span>
              </div>
              <div className="text-sm text-green-800 dark:text-green-200">
                Earn an estimated ${savingsGoal.interestEarned.toLocaleString()} in interest with a 4% APY account
              </div>
            </div>
          )}
        </div>

        <div>
          <h4 className="font-medium text-foreground mb-3">Savings Scenarios</h4>
          <div className="space-y-2">
            {savingsGoal.scenarios.slice(0, 6).map((scenario, idx) => (
              <div 
                key={idx} 
                className={`p-3 rounded-lg border ${scenario.recommended ? 'border-primary bg-primary/5' : 'border-border'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-foreground">{scenario.name}</div>
                  {scenario.recommended && (
                    <span className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground">Recommended</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Monthly: </span>
                    <span className="font-medium text-foreground">${scenario.monthlyContribution.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total: </span>
                    <span className="font-medium text-foreground">${scenario.totalAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
